import { AlertTriangle, CheckCircle2, ListChecks, Scale, Target } from "lucide-react";
import { useLocale, useTranslation, legalBasisText } from "@ncp/i18n";
import { Badge, Card, DonutChart, StackedBars, TrendLine } from "@ncp/ui";
import { fmtAed, hhiBand, hhiText, type ChartSpec, type IndicatorSeries, type StudyAnalysis, type StudyDetail } from "./api";

const likelihoodTone = (v: string): "danger" | "warning" | "success" | "outline" => {
  const r = v.trim().toLowerCase();
  if (r.startsWith("medium")) return "warning";
  if (r.startsWith("high")) return "danger";
  if (r.startsWith("low")) return "success";
  return "outline";
};
const assessmentTone = (v: string): "danger" | "warning" | "success" | "outline" => {
  const r = v.trim().toLowerCase();
  if (r.startsWith("concern")) return "danger";
  if (r.startsWith("neutral")) return "warning";
  if (r.startsWith("favourable") || r.startsWith("favorable")) return "success";
  return "outline";
};
/**
 * Ratings arrive as "High", "Medium–high" or "High on the facts; whether it amounts to tying requires assessment".
 * Only the leading rating token belongs in the badge; everything after it is shown as a note.
 */
const splitRating = (v: string): [string, string] => {
  const text = (v ?? "").trim();
  const m = /^(low|medium|high|favou?rable|neutral|concern)(?:\s*[-–—/]\s*(?:low|medium|high))?/i.exec(text);
  if (!m) return [text, ""];
  const rating = m[0];
  const note = text.slice(rating.length).replace(/^\s*[–—:;,.]\s*/, "").trim();
  return [rating, note];
};

function Panel({ title, icon: Icon, children }: { title: string; icon: typeof Scale; children: React.ReactNode }) {
  return (
    <Card className="p-6">
      <h4 className="mb-4 flex items-center gap-2 text-base font-semibold text-aeblack-950"><Icon className="size-4 text-aegold-700" /> {title}</h4>
      {children}
    </Card>
  );
}

/** Leading share from the latest-year donut the agent produced (labels other than "Others"). */
function leaderFromCharts(charts: ChartSpec[] | null | undefined): { name: string; value: number } | null {
  const donut = charts?.find((c) => c.kind === "donut" && c.unit === "%" && /share|حصص/i.test(c.title));
  if (!donut) return null;
  const rows = donut.labels.map((name, i) => ({ name, value: donut.series[0]?.values[i] ?? 0 })).filter((d) => !/^(others?|أخرى)$/i.test(d.name.trim()));
  return rows.sort((a, b) => b.value - a.value)[0] ?? null;
}

/** Structured analysis of a published (or drafted) market study: definition, findings, theories of harm, indicators, recommendations. */
export function StudyAnalysisPanels({ study }: { study: StudyDetail }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const a: StudyAnalysis = study.analysis ?? {};
  const leader = leaderFromCharts(study.charts);
  const m = study.market;
  const band = hhiBand(m?.hhi);
  const priceGap = (() => {
    const c = study.charts?.find((x) => x.kind === "line" && x.series.length >= 2 && /price|سعر/i.test(x.title));
    if (!c) return null;
    const last = (s: { values: number[] }) => s.values[s.values.length - 1] ?? 0;
    return { uae: last(c.series[0]!), benchmark: last(c.series[1]!), year: c.labels[c.labels.length - 1] };
  })();
  const glance = [
    m?.sizeAed ? { label: t("backoffice:dataBank.size"), value: `${fmtAed(m.sizeAed, locale)}`, sub: m.sizeYear ? String(m.sizeYear) : undefined } : null,
    m?.hhi != null ? { label: "HHI", value: hhiText(m.hhi, locale), sub: t(`backoffice:dataBank.hhi.${band}`), tone: band } : null,
    m?.cr4 != null ? { label: "CR4", value: `${m.cr4}%` } : null,
    leader ? { label: t("backoffice:dataBank.analysis.leader"), value: `${leader.value}%`, sub: leader.name } : null,
    priceGap ? { label: t("backoffice:dataBank.analysis.priceGap"), value: `${priceGap.uae} / ${priceGap.benchmark}`, sub: priceGap.year } : null,
  ].filter(Boolean) as Array<{ label: string; value: string; sub?: string; tone?: string }>;

  return (
    <div className="space-y-5">
      {glance.length > 0 ? (
        <section aria-label={t("backoffice:dataBank.analysis.atAGlance")} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {glance.map((g) => (
            <Card key={g.label} className="p-4">
              <p className="text-xs font-medium text-sand-700">{g.label}</p>
              <p className={`mt-1.5 font-heading text-2xl font-semibold tabular ${g.tone === "high" ? "text-danger-700" : g.tone === "moderate" ? "text-warning-700" : "text-aeblack-950"}`}>{g.value}</p>
              {g.sub ? <p className="mt-0.5 truncate text-xs text-muted-foreground" title={g.sub}>{g.sub}</p> : null}
            </Card>
          ))}
        </section>
      ) : null}

      {a.definition ? (
        <Panel title={t("backoffice:dataBank.analysis.definition")} icon={Scale}>
          <dl className="grid gap-4 md:grid-cols-2">
            <div><dt className="text-xs font-medium text-sand-700">{t("backoffice:dataBank.analysis.product")}</dt><dd className="mt-1 text-sm text-aeblack-950">{a.definition.product}</dd></div>
            <div><dt className="text-xs font-medium text-sand-700">{t("backoffice:dataBank.analysis.geographic")}</dt><dd className="mt-1 text-sm text-aeblack-950">{a.definition.geographic}</dd></div>
            <div className="md:col-span-2"><dt className="text-xs font-medium text-sand-700">{t("backoffice:dataBank.analysis.rationale")}</dt><dd className="mt-1 text-sm leading-relaxed text-aeblack-900/85">{a.definition.rationale}</dd></div>
            {a.definition.adjacentMarkets?.length ? (
              <div className="md:col-span-2"><dt className="text-xs font-medium text-sand-700">{t("backoffice:dataBank.analysis.adjacentMarkets")}</dt><dd className="mt-1.5 flex flex-wrap gap-1.5">{a.definition.adjacentMarkets.map((x) => <Badge key={x} variant="outline" size="sm">{x}</Badge>)}</dd></div>
            ) : null}
          </dl>
        </Panel>
      ) : null}

      {a.keyFindings?.length ? (
        <Panel title={t("backoffice:dataBank.analysis.keyFindings")} icon={ListChecks}>
          <ol className="space-y-2.5">
            {a.keyFindings.map((f, i) => (
              <li key={i} className="grid grid-cols-[2rem_minmax(0,1fr)] gap-3 text-sm leading-relaxed text-aeblack-900">
                <span className="font-mono text-xs tabular text-aegold-800">{String(i + 1).padStart(2, "0")}</span>
                <span>{f}</span>
              </li>
            ))}
          </ol>
        </Panel>
      ) : null}

      {a.theoriesOfHarm?.length ? (
        <Panel title={t("backoffice:dataBank.analysis.theoriesOfHarm")} icon={AlertTriangle}>
          <ol className="divide-y divide-sand-100">
            {a.theoriesOfHarm.map((th, i) => {
              const [rating, note] = splitRating(th.likelihood);
              return (
                <li key={i} className="grid gap-3 py-4 first:pt-0 last:pb-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs tabular text-aegold-800">{String(i + 1).padStart(2, "0")}</span>
                    <h5 className="text-sm font-semibold text-aeblack-950">{th.name}</h5>
                    <Badge variant={likelihoodTone(rating)} size="sm" dot className="ms-auto">{t("backoffice:dataBank.analysis.likelihood")}: {t(`backoffice:dataBank.analysis.likelihoodValue.${rating}`, { defaultValue: rating })}</Badge>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div><p className="text-xs font-medium text-sand-700">{t("backoffice:dataBank.analysis.mechanism")}</p><p className="mt-1 text-sm leading-relaxed text-aeblack-900/85">{th.mechanism}</p></div>
                    <div><p className="text-xs font-medium text-sand-700">{t("backoffice:dataBank.analysis.evidence")}</p><p className="mt-1 text-sm leading-relaxed text-aeblack-900/85">{th.evidence}</p></div>
                  </div>
                  {note ? <p className="text-xs leading-relaxed text-muted-foreground">{note}</p> : null}
                </li>
              );
            })}
          </ol>
        </Panel>
      ) : null}

      {a.indicatorAssessments?.length ? (
        <Panel title={t("backoffice:dataBank.analysis.indicators")} icon={Target}>
          <ul className="grid gap-3 md:grid-cols-2">
            {a.indicatorAssessments.map((ind, i) => {
              const [rating, note] = splitRating(ind.assessment);
              return (
                <li key={i} className="rounded-xl border border-sand-200 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-aeblack-950">{ind.indicator}</p>
                    <Badge variant={assessmentTone(rating)} size="sm" dot className="shrink-0">{t(`backoffice:dataBank.analysis.assessmentValue.${rating}`, { defaultValue: rating })}</Badge>
                  </div>
                  <dl className="mt-3 grid grid-cols-[minmax(0,6rem)_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-xs">
                    <dt className="text-sand-700">{t("backoffice:dataBank.analysis.latest")}</dt><dd className="tabular text-aeblack-950">{ind.latest}</dd>
                    <dt className="text-sand-700">{t("backoffice:dataBank.analysis.trend")}</dt><dd className="text-aeblack-900/85">{ind.trend}</dd>
                    <dt className="text-sand-700">{t("backoffice:dataBank.analysis.benchmark")}</dt><dd className="text-aeblack-900/85">{ind.benchmark}</dd>
                  </dl>
                  {note ? <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{note}</p> : null}
                </li>
              );
            })}
          </ul>
        </Panel>
      ) : null}

      {a.recommendations?.length ? (
        <Panel title={t("backoffice:dataBank.analysis.recommendations")} icon={CheckCircle2}>
          <ol className="divide-y divide-sand-100">
            {a.recommendations.map((r, i) => (
              <li key={i} className="grid gap-3 py-3 first:pt-0 last:pb-0 md:grid-cols-[2rem_minmax(0,1fr)_minmax(0,12rem)_minmax(0,10rem)_minmax(0,14rem)] md:items-start">
                <span className="font-mono text-xs tabular text-aegold-800">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-sm leading-relaxed text-aeblack-950">{r.action}</p>
                <p className="text-xs text-muted-foreground"><span className="font-medium text-sand-700">{t("backoffice:dataBank.analysis.owner")}:</span> {r.owner}</p>
                <p className="text-xs text-muted-foreground"><span className="font-medium text-sand-700">{t("backoffice:dataBank.analysis.timeline")}:</span> {r.timeline}</p>
                <p className="text-xs text-muted-foreground"><span className="font-medium text-sand-700">{t("backoffice:dataBank.analysis.legalBasis")}:</span> {legalBasisText(r.legalBasis, locale)}</p>
              </li>
            ))}
          </ol>
          {a.monitoringKpis?.length ? (
            <div className="mt-5 border-t border-sand-200 pt-4">
              <p className="mb-2 text-xs font-medium text-sand-700">{t("backoffice:dataBank.analysis.monitoringKpis")}</p>
              <ul className="flex flex-wrap gap-2">{a.monitoringKpis.map((k, i) => <li key={i} className="rounded-lg border border-sand-200 bg-sand-50 px-2.5 py-1 text-xs text-aeblack-900">{k}</li>)}</ul>
            </div>
          ) : null}
        </Panel>
      ) : null}
    </div>
  );
}

/** Indicator series of a market: time series as lines, breakdowns as donuts (shares) or bars (counts). */
export function IndicatorGrid({ series }: { series: IndicatorSeries[] }) {
  const { locale } = useLocale();
  if (series.length === 0) return null;
  const timeSeries = series.filter((s) => !s.dimensional);
  const breakdowns = series.filter((s) => s.dimensional);
  // Pair the UAE price index with its benchmark on one chart when both exist.
  const paired = new Set<string>();
  const charts: Array<{ key: string; title: string; unit: string; source: string; node: React.ReactNode }> = [];
  for (const s of timeSeries) {
    if (paired.has(s.key)) continue;
    const partner = s.key === "price_index_uae" ? timeSeries.find((x) => x.key === "price_index_benchmark") : null;
    if (partner) paired.add(partner.key);
    const years = Array.from(new Set([...s.points, ...(partner?.points ?? [])].map((p) => p.year))).sort();
    const lines = [s, ...(partner ? [partner] : [])].map((x) => ({ name: locale === "ar" ? x.labelAr : x.label, data: years.map((y) => x.points.find((p) => p.year === y)?.value ?? 0) }));
    charts.push({ key: s.key, title: locale === "ar" ? s.labelAr : s.label, unit: s.unit, source: s.source, node: <TrendLine categories={years.map(String)} series={lines} height={220} /> });
  }
  for (const b of breakdowns) {
    const year = Math.max(...b.points.map((p) => p.year));
    const pts = b.points.filter((p) => p.year === year).sort((x, y) => y.value - x.value);
    const data = pts.map((p) => ({ name: p.dimension ?? "?", value: p.value }));
    charts.push({
      key: b.key,
      title: `${locale === "ar" ? b.labelAr : b.label} (${year})`,
      unit: b.unit,
      source: b.source,
      node: b.unit === "%" ? <DonutChart data={data} height={230} legend="right" /> : <StackedBars categories={data.map((d) => d.name)} series={[{ name: b.unit, data: data.map((d) => d.value) }]} height={230} />,
    });
  }
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {charts.map((c) => (
        <Card key={c.key} className="p-4">
          <div className="mb-2 flex items-baseline justify-between gap-2"><p className="text-sm font-semibold">{c.title}</p><span className="text-[11px] text-muted-foreground">{c.unit} · {c.source}</span></div>
          {c.node}
        </Card>
      ))}
    </div>
  );
}
